import _ from 'lodash'
import { useEffect, useState } from 'react'

import {
  fetchDistinctFieldValues as fetchDistinctFieldValuesWith,
  fetchReferenceListOptions as fetchReferenceListOptionsWith,
  getEntityOwnerFirmId,
  IOptionSource,
  IReferenceListOption,
  resolveReferenceListFirmId,
} from 'shared-libs/helpers/reference-list'

import apis from 'browser/app/models/apis'

/**
 * Web binding for the platform-agnostic option-source resolver
 * (shared-libs/helpers/reference-list): supplies the browser `apis` instance and
 * React hooks, and re-exports the shared pure logic for a single import site.
 */

export {
  getOptionSource,
  getReferenceListKey,
  getEntityOwnerFirmId,
  resolveReferenceListFirmId,
  resolveEntityTypeId,
  applyTagTransform,
  mapReferenceListItemsToOptions,
  IReferenceListOption,
  IOptionSource,
} from 'shared-libs/helpers/reference-list'

/** Loads a firm's reference list by key, scoped to `firmId` (the entity owner firm) when given. */
export const fetchReferenceListOptions = (
  referenceListKey?: string,
  firmId?: string,
): Promise<IReferenceListOption[]> => fetchReferenceListOptionsWith(apis, referenceListKey, firmId)

/** Loads distinct existing values for a bound field, scoped to `firmId` (the entity owner firm) when given. */
export const fetchDistinctFieldValues = (
  entityType: string,
  valuePath: string[],
  size?: number,
  firmId?: string,
): Promise<IReferenceListOption[]> => fetchDistinctFieldValuesWith(apis, entityType, valuePath, size, firmId)

export interface IUseReferenceListFirmId {
  /** The firm whose reference list to load, per the fallback ladder. */
  firmId: string | undefined
  /** True while an edge-based firm is still resolving — hold the list load until it settles. */
  resolving: boolean
}

/** Reads the edge target id for an `optionSource.referenceListFirmEdge`, if configured/populated. */
const getFirmEdgeTargetId = (entity: any, optionSource?: IOptionSource): string | undefined => {
  const edgePath = optionSource?.referenceListFirmEdge
  if (!edgePath) {
    return undefined
  }
  const edge = entity?.get?.(edgePath) ?? _.get(entity, edgePath)
  return _.get(edge, 'entityId')
}

/**
 * Resolves the firm whose reference list governs a field (see
 * {@link resolveReferenceListFirmId}). No edge → the owner firm, synchronously.
 * With an edge, resolves the target's owner firm async; `resolving` stays true
 * until it settles so the caller doesn't fetch against the wrong firm meanwhile.
 */
export const useReferenceListFirmId = (entity: any, optionSource?: IOptionSource): IUseReferenceListFirmId => {
  const ownerFirmId = getEntityOwnerFirmId(entity)
  const edgeTargetId = getFirmEdgeTargetId(entity, optionSource)
  const hasEdge = !!optionSource?.referenceListFirmEdge && !!edgeTargetId

  const [state, setState] = useState<IUseReferenceListFirmId>(() =>
    hasEdge ? { firmId: undefined, resolving: true } : { firmId: ownerFirmId, resolving: false }
  )

  useEffect(() => {
    if (!hasEdge) {
      setState({ firmId: ownerFirmId, resolving: false })
      return
    }
    let cancelled = false
    setState({ firmId: undefined, resolving: true })
    void resolveReferenceListFirmId(apis, entity, optionSource).then((firmId) => {
      if (!cancelled) {
        setState({ firmId, resolving: false })
      }
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasEdge, edgeTargetId, ownerFirmId])

  return state
}

export interface IUseReferenceListOptions {
  options: IReferenceListOption[]
  isLoading: boolean
  /** true once a load attempt has settled (so callers know when [] means "no list"). */
  loaded: boolean
}

/**
 * Hook wrapper around {@link fetchReferenceListOptions}. Loads eagerly on mount
 * (and when the key or firm changes) so options are ready before the menu opens.
 */
export const useReferenceListOptions = (
  referenceListKey?: string,
  firmId?: string,
): IUseReferenceListOptions => {
  const [options, setOptions] = useState<IReferenceListOption[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    let cancelled = false
    if (!referenceListKey) {
      setOptions([])
      setIsLoading(false)
      setLoaded(true)
      return
    }
    setLoaded(false)
    setIsLoading(true)
    fetchReferenceListOptions(referenceListKey, firmId)
      .then((opts) => {
        if (!cancelled) {
          setOptions(opts)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false)
          setLoaded(true)
        }
      })
    return () => {
      cancelled = true
    }
  }, [referenceListKey, firmId])

  return { options, isLoading, loaded }
}

/**
 * Hook that loads the firm's distinct existing values for a bound field. Pass a
 * falsy `entityType` (or empty `valuePath`) to skip the load — callers use this
 * to only fetch distinct values when `optionSource.distinctValues` is enabled
 * and no reference list supplied options.
 */
export const useDistinctFieldValues = (
  entityType: string | undefined,
  valuePath: string[],
  size?: number,
  firmId?: string,
): IUseReferenceListOptions => {
  const [options, setOptions] = useState<IReferenceListOption[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const valuePathKey = (valuePath ?? []).join('.')

  useEffect(() => {
    let cancelled = false
    if (!entityType || !valuePathKey) {
      setOptions([])
      setIsLoading(false)
      setLoaded(true)
      return
    }
    setLoaded(false)
    setIsLoading(true)
    fetchDistinctFieldValues(entityType, valuePathKey.split('.'), size, firmId)
      .then((opts) => {
        if (!cancelled) {
          setOptions(opts)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false)
          setLoaded(true)
        }
      })
    return () => {
      cancelled = true
    }
  }, [entityType, valuePathKey, size, firmId])

  return { options, isLoading, loaded }
}
